import { resolveSketchRegions } from '../../sketch-analysis';
import {
    resyncProfileRegionGeometryOuterFromSegments,
} from './offsetProfileRegionGeometry';
import {
    doesProfileRegionGeometryMatchClosedRuntimeSpline,
    getRuntimeSketchEntityId,
    isClosedRuntimeSplineLikeEntity,
} from './profileSplinePredicate';

/**
 * 持久化区域与运行时草图恢复输入。
 */
export interface ProfileRegionRuntimeRecoveryInput {
    /** 草图 ID。 */
    sketchId: string;
    /** 旧区域几何；可能只包含采样点列。 */
    fallbackRegionGeometries: ProfileRegionGeometry[];
    /** 选中轮廓键；通常对应闭合草图对象 ID。 */
    contourKeys?: string[];
    /** 运行时草图平面快照。 */
    runtimeSketchPlane?: RuntimeSketchPlaneInput;
    /** 运行时草图实体快照。 */
    runtimeSketchEntities?: RuntimeSketchEntityInput[];
}

/**
 * 判断区域几何是否缺少精确曲线段。
 *
 * @param regionGeometry 区域几何。
 * @returns 缺少精确曲线段时返回 true。
 */
export function lacksPreciseRegionSegments(regionGeometry: ProfileRegionGeometry): boolean {
    if (!Array.isArray(regionGeometry.outerSegments) || regionGeometry.outerSegments.length <= 0) {
        return true;
    }
    if (!Array.isArray(regionGeometry.holes) || regionGeometry.holes.length <= 0) {
        return false;
    }
    return !Array.isArray(regionGeometry.holeSegments)
        || regionGeometry.holeSegments.length !== regionGeometry.holes.length
        || regionGeometry.holeSegments.some((segments) => !Array.isArray(segments) || segments.length <= 0);
}

/**
 * 从运行时草图快照重建区域几何。
 *
 * @param input 恢复输入。
 * @returns 由区域解析器重建出的区域几何。
 */
export function rebuildProfileRegionGeometriesFromRuntimeSketch(
    input: Pick<ProfileRegionRuntimeRecoveryInput, 'sketchId' | 'runtimeSketchPlane' | 'runtimeSketchEntities'>,
): ProfileRegionGeometry[] {
    if (!input.runtimeSketchPlane || !Array.isArray(input.runtimeSketchEntities) || input.runtimeSketchEntities.length <= 0) {
        return [];
    }
    return resolveSketchRegions(
        input.runtimeSketchEntities,
        input.runtimeSketchPlane,
        input.sketchId,
    ).map((region) => resyncProfileRegionGeometryOuterFromSegments({
        outer: region.outer.map((point) => [...point] as [number, number, number]),
        holes: region.holes.map((hole) => hole.map((point) => [...point] as [number, number, number])),
        normal: [...region.normal] as [number, number, number],
        xAxis: [...region.xAxis] as [number, number, number],
        yAxis: [...region.yAxis] as [number, number, number],
        outerSegments: region.outerSegments?.map((segment) => structuredClone(segment)),
        holeSegments: region.holeSegments?.map((segments) => segments.map((segment) => structuredClone(segment))),
    }));
}

/**
 * 同步区域集合 outer 与精确曲线段。
 *
 * @param regions 区域几何集合。
 * @returns 同步后的区域几何集合。
 */
function syncProfileRegionGeometryCollection(
    regions: ProfileRegionGeometry[],
): ProfileRegionGeometry[] {
    return regions.map((region) => resyncProfileRegionGeometryOuterFromSegments(region));
}

/**
 * 克隆外环精确曲线段。
 *
 * @param regionGeometry 区域几何。
 * @returns 外环曲线段副本。
 */
function cloneOuterSegments(regionGeometry: ProfileRegionGeometry): ProfileCurveSegment[] | undefined {
    return regionGeometry.outerSegments?.map((segment) => structuredClone(segment));
}

/**
 * 克隆内环精确曲线段。
 *
 * @param regionGeometry 区域几何。
 * @returns 内环曲线段副本。
 */
function cloneHoleSegments(regionGeometry: ProfileRegionGeometry): ProfileCurveSegment[][] | undefined {
    return regionGeometry.holeSegments?.map((segments) => segments.map((segment) => structuredClone(segment)));
}

/**
 * 生成外环曲线段比较键。
 *
 * @param regionGeometry 区域几何。
 * @returns 比较键；没有外环曲线段时返回 null。
 */
function buildOuterSegmentComparisonKey(regionGeometry: ProfileRegionGeometry): string | null {
    if (!Array.isArray(regionGeometry.outerSegments) || regionGeometry.outerSegments.length <= 0) {
        return null;
    }
    return JSON.stringify(regionGeometry.outerSegments);
}

/**
 * 通过实体 ID 建立运行时草图实体索引。
 *
 * @param entities 运行时草图实体。
 * @returns 实体索引。
 */
function buildRuntimeEntityMap(entities: RuntimeSketchEntityInput[] | undefined): Map<string, RuntimeSketchEntityInput> {
    const entityMap = new Map<string, RuntimeSketchEntityInput>();
    if (!Array.isArray(entities)) {
        return entityMap;
    }
    entities.forEach((entity) => {
        const entityId = getRuntimeSketchEntityId(entity);
        if (entityId) {
            entityMap.set(entityId, entity);
        }
    });
    return entityMap;
}

/**
 * 判断缺失精确段的 fallback 是否会把已知闭合样条降级为折线。
 *
 * @param input 恢复输入。
 * @param fallbackRegionGeometries 准备回退的区域几何。
 * @returns 会降级闭合样条时返回 true。
 */
function wouldDowngradeKnownClosedSpline(
    input: ProfileRegionRuntimeRecoveryInput,
    fallbackRegionGeometries: ProfileRegionGeometry[],
): boolean {
    const entityMap = buildRuntimeEntityMap(input.runtimeSketchEntities);
    if (entityMap.size <= 0) {
        return false;
    }

    const closedSplineEntities = Array.from(entityMap.values()).filter((entity) => isClosedRuntimeSplineLikeEntity(entity));
    if (closedSplineEntities.length <= 0) {
        return false;
    }

    return fallbackRegionGeometries.some((regionGeometry, index) => {
        if (!lacksPreciseRegionSegments(regionGeometry)) {
            return false;
        }
        const contourKey = input.contourKeys?.[index];
        if (contourKey) {
            const entity = entityMap.get(contourKey);
            if (entity && isClosedRuntimeSplineLikeEntity(entity)) {
                return true;
            }
        }
        if (closedSplineEntities.some((entity) => (
            doesProfileRegionGeometryMatchClosedRuntimeSpline(regionGeometry, entity)
        ))) {
            return true;
        }
        return !contourKey
            && fallbackRegionGeometries.length === 1
            && closedSplineEntities.length === 1;
    });
}

/**
 * 阻止已知闭合样条轮廓退化为点列建模。
 *
 * @param input 恢复输入。
 * @param fallbackRegionGeometries 准备回退的区域几何。
 */
function assertNoClosedSplinePolylineFallback(
    input: ProfileRegionRuntimeRecoveryInput,
    fallbackRegionGeometries: ProfileRegionGeometry[],
): void {
    if (!wouldDowngradeKnownClosedSpline(input, fallbackRegionGeometries)) {
        return;
    }
    throw new Error('闭合样条轮廓缺少精确曲线段，已阻止退化为折线拉伸。请重新选择该草图轮廓后再拉伸。');
}

/**
 * 尝试把 runtime 恢复出的唯一精确曲线段合并回旧 fallback。
 *
 * @param fallbackRegionGeometries 旧 fallback 区域几何。
 * @param runtimeRegionGeometries runtime 解析出的区域几何。
 * @returns 合并后的区域几何；无法安全合并时返回 null。
 */
function mergeUniqueRuntimeSegmentsIntoSingleFallback(
    fallbackRegionGeometries: ProfileRegionGeometry[],
    runtimeRegionGeometries: ProfileRegionGeometry[],
): ProfileRegionGeometry[] | null {
    if (fallbackRegionGeometries.length !== 1 || !lacksPreciseRegionSegments(fallbackRegionGeometries[0]!)) {
        return null;
    }

    const candidateRegions = runtimeRegionGeometries.filter((regionGeometry) => (
        Array.isArray(regionGeometry.outerSegments) && regionGeometry.outerSegments.length > 0
    ));
    const comparisonKeys = new Set(
        candidateRegions
            .map((regionGeometry) => buildOuterSegmentComparisonKey(regionGeometry))
            .filter((key): key is string => key !== null),
    );
    if (candidateRegions.length <= 0 || comparisonKeys.size !== 1) {
        return null;
    }

    const segmentSource = candidateRegions[0]!;
    return [{
        ...fallbackRegionGeometries[0]!,
        outerSegments: cloneOuterSegments(segmentSource),
        holeSegments: cloneHoleSegments(segmentSource),
    }];
}

/**
 * 优先用运行时草图快照补齐缺失的精确曲线段。
 *
 * 设计说明：
 * 1. 旧数据或中间链路可能只保留区域采样点列，直接建模会把样条降级为折线；
 * 2. 运行时草图实体仍然携带 circle、ellipse、spline 等真实语义时，应统一回到区域解析器重建；
 * 3. 当明确选中目标是闭合样条且恢复失败时直接阻断，避免预览或提交生成错误的折线 BRep。
 *
 * @param input 恢复输入。
 * @returns 补齐后的区域几何。
 */
export function resolveProfileRegionGeometriesFromRuntime(
    input: ProfileRegionRuntimeRecoveryInput,
): ProfileRegionGeometry[] {
    const clonedFallbacks = input.fallbackRegionGeometries.map((regionGeometry) => structuredClone(regionGeometry));
    const mustRebuildFromRuntime = clonedFallbacks.length <= 0
        || clonedFallbacks.some((regionGeometry) => lacksPreciseRegionSegments(regionGeometry));
    if (!mustRebuildFromRuntime) {
        assertNoClosedSplinePolylineFallback(input, clonedFallbacks);
        return syncProfileRegionGeometryCollection(clonedFallbacks);
    }

    const runtimeRegionGeometries = rebuildProfileRegionGeometriesFromRuntimeSketch(input);
    if (runtimeRegionGeometries.length <= 0) {
        assertNoClosedSplinePolylineFallback(input, clonedFallbacks);
        return syncProfileRegionGeometryCollection(clonedFallbacks);
    }
    if (clonedFallbacks.length <= 0 || runtimeRegionGeometries.length === clonedFallbacks.length) {
        assertNoClosedSplinePolylineFallback(input, runtimeRegionGeometries);
        return runtimeRegionGeometries;
    }
    if (clonedFallbacks.length === 1 && runtimeRegionGeometries.length === 1) {
        assertNoClosedSplinePolylineFallback(input, runtimeRegionGeometries);
        return runtimeRegionGeometries;
    }
    const mergedFallbacks = mergeUniqueRuntimeSegmentsIntoSingleFallback(clonedFallbacks, runtimeRegionGeometries);
    if (mergedFallbacks) {
        assertNoClosedSplinePolylineFallback(input, mergedFallbacks);
        return syncProfileRegionGeometryCollection(mergedFallbacks);
    }
    assertNoClosedSplinePolylineFallback(input, clonedFallbacks);
    return syncProfileRegionGeometryCollection(clonedFallbacks);
}
